import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import {
  SUBMISSION_LANGUAGES,
  SUBMISSION_TYPES,
  FACETS,
  IDENTITY_MODES,
  CHALLENGES,
  type SubmissionType,
  type SubmissionLanguage,
  type IdentityMode,
  type FacetKey,
} from "@/lib/submit-shared";
import { CATEGORIES } from "@/lib/categories";
import { pickWelcome, type WelcomeMessage } from "@/i18n/submit-welcome";
import { submitStory } from "@/lib/submit.functions";
import type { Lang } from "@/lib/i18n";

// Wizard-local strings. EN canonical; RU per §3. Other locales fall back to EN
// until translators land them.
type WizardStrings = {
  privacy: string;
  safety_h: string; safety_p: string; safety_help: string; safety_q: string;
  side_victim: string; side_victim_d: string;
  side_confessor: string; side_confessor_d: string;
  lang_h: string; lang_p: string;
  story_h: string; story_p: string; story_placeholder: string;
  story_chars: (n: number) => string; story_too_short: string;
  companion_open: string; companion_open_d: string;
  companion_label: string; companion_send: string; companion_placeholder: string;
  facets_h: string; facets_p: string;
  facets_categories: string;
  facets_skip: string;
  facet_labels: Record<FacetKey, string>;
  identity_h: string; identity_p: string;
  id_anonymous: string; id_anonymous_d: string;
  id_pseudo: string; id_pseudo_d: string;
  id_pseudo_contact: string; id_pseudo_contact_d: string;
  pseudo_label: string; contact_label: string;
  consent_publish: string;
  consent_redact: string;
  consent_translate: string;
  review_h: string; review_p: string;
  sum_type: string; sum_lang: string; sum_length: string;
  sum_categories: string; sum_identity: string;
  sum_chars: (n: number) => string;
  translate_all: string; translate_all_d: string;
  challenge_legend: string;
  send: string; sending: string; back: string; cont: string;
  sent_h: string; sent_p: string; sent_read: string; sent_help: string;
};

const STRINGS_EN: WizardStrings = {
  privacy: "No IP addresses · No analytics · No fingerprinting on this page",
  safety_h: "Before you begin",
  safety_p:
    "You can tell your story without giving your name, your ship's name, or any detail that identifies you. We will help you remove identifying details. Submitting is free and requires no account.",
  safety_help: "In danger right now? → Open Help",
  safety_q: "Which side of the story is this?",
  side_victim: "I experienced this",
  side_victim_d: "I or someone I witnessed was on the receiving end.",
  side_confessor: "I was on the other side",
  side_confessor_d: "I acted as the perpetrator and want to set it down. Anonymous, no judgement.",
  lang_h: "What language will you write in?",
  lang_p: "Write in the language you think and dream in. We will translate.",
  story_h: "Your story",
  story_p:
    "What happened? Over what period? How did it affect you? How did it end? Minimum 300 characters. Closing this tab erases your draft — by design.",
  story_placeholder: "Write in your own language. The companion on the right is optional.",
  story_chars: (n) => `${n} / 300+ characters`,
  story_too_short: "Write at least 300 characters to continue.",
  companion_open: "Talk with the AI companion",
  companion_open_d:
    "Not a lawyer, not a crisis service, not a human. It will not store your words until you press Send below.",
  companion_label: "AI companion",
  companion_send: "Send",
  companion_placeholder: "Type to the companion…",
  facets_h: "Tags & categories",
  facets_p:
    "Everything here is optional. Skip any field. Region and decade only — never specific enough to identify a vessel.",
  facets_categories: "Categories",
  facets_skip: "— skip —",
  facet_labels: {
    abuser_position: "Abuser's position",
    victim_position: "Position on board",
    vessel_type: "Vessel type",
    region: "Region / flag category",
    outcome: "Outcome",
    decade: "Time frame (decade)",
  },
  identity_h: "Identity & consent",
  identity_p:
    "Most authors stay fully anonymous. A pseudonym is shown on the entry; a contact email is admin-only and used only if a moderator needs a clarification.",
  id_anonymous: "Fully anonymous",
  id_anonymous_d: "Author shown as \u201CAnonymous seafarer\u201D.",
  id_pseudo: "Pseudonym",
  id_pseudo_d: "Pick a name. We never check it against anything.",
  id_pseudo_contact: "Pseudonym + private contact email",
  id_pseudo_contact_d:
    "Email is encrypted at rest, admin-only, used solely for clarifications. Never displayed.",
  pseudo_label: "Pseudonym",
  contact_label: "Contact email (admin-only)",
  consent_publish:
    "By pressing Send you consent to publication on madcaptains.net after human review and redaction.",
  consent_redact: "I consent to editorial redaction of identifying details before publication",
  consent_translate: "I consent to translation into other languages",
  review_h: "Review & send",
  review_p:
    "A human moderator will read this. Identifying details are removed before publication. If anything cannot be published, you will receive a reasoned rejection (only if you provided a contact email). Otherwise the entry is published quietly.",
  sum_type: "Type", sum_lang: "Language", sum_length: "Length",
  sum_categories: "Categories", sum_identity: "Identity",
  sum_chars: (n) => `${n} characters`,
  translate_all: "Translate to all site languages",
  translate_all_d:
    "On publication, queue AI translations into every active locale. Each translation is labeled as AI and a human translator may refine it. On by default.",
  challenge_legend: "One quick check — not Google reCAPTCHA",
  send: "Send my story", sending: "Sending…",
  back: "← Back", cont: "Continue →",
  sent_h: "Received.",
  sent_p:
    "Your story is in the moderation queue. A human will read it. If you provided a contact email and a clarification is needed, you will hear back. Otherwise, watch the archive — your entry may appear under Anonymous seafarer or your chosen pseudonym.",
  sent_read: "Read the archive", sent_help: "Get help",
};

const STRINGS_RU: WizardStrings = {
  privacy: "Без IP · Без аналитики · Без fingerprinting на этой странице",
  safety_h: "Прежде чем начать",
  safety_p:
    "Вы можете рассказать историю, не называя ни себя, ни судна, ни деталей, по которым вас можно опознать. Мы поможем убрать идентифицирующие детали. Это бесплатно и без регистрации.",
  safety_help: "Опасность прямо сейчас? → Откройте «Помощь»",
  safety_q: "С какой стороны эта история?",
  side_victim: "Я это пережил(а)",
  side_victim_d: "Я или тот, кого я видел(а), оказались на принимающей стороне.",
  side_confessor: "Я был(а) с другой стороны",
  side_confessor_d:
    "Я действовал(а) как тот, кто причинял вред, и хочу это записать. Анонимно, без осуждения.",
  lang_h: "На каком языке вы будете писать?",
  lang_p: "Пишите на том языке, на котором думаете и видите сны. Мы переведём.",
  story_h: "Ваша история",
  story_p:
    "Что произошло? В какой период? Как это на вас повлияло? Чем закончилось? Минимум 300 символов. Закрытие вкладки удалит черновик — намеренно.",
  story_placeholder: "Пишите на своём языке. Собеседник справа — по желанию.",
  story_chars: (n) => `${n} / 300+ символов`,
  story_too_short: "Напишите минимум 300 символов, чтобы продолжить.",
  companion_open: "Поговорить с ИИ-собеседником",
  companion_open_d:
    "Не юрист, не служба кризисной помощи, не человек. Он не сохраняет ваши слова, пока вы не нажмёте «Отправить» ниже.",
  companion_label: "ИИ-собеседник",
  companion_send: "Отпр.",
  companion_placeholder: "Напишите собеседнику…",
  facets_h: "Теги и категории",
  facets_p:
    "Всё здесь — по желанию. Любое поле можно пропустить. Только регион и десятилетие — недостаточно конкретно, чтобы опознать судно.",
  facets_categories: "Категории",
  facets_skip: "— пропустить —",
  facet_labels: {
    abuser_position: "Должность абьюзера",
    victim_position: "Ваша должность на борту",
    vessel_type: "Тип судна",
    region: "Регион / категория флага",
    outcome: "Исход",
    decade: "Период (десятилетие)",
  },
  identity_h: "Идентичность и согласие",
  identity_p:
    "Большинство авторов остаются полностью анонимными. Псевдоним показывается у записи; контактный email доступен только модераторам и используется лишь при необходимости уточнения.",
  id_anonymous: "Полностью анонимно",
  id_anonymous_d: "Автор отображается как «Anonymous seafarer».",
  id_pseudo: "Псевдоним",
  id_pseudo_d: "Выберите имя. Мы его ни с чем не сверяем.",
  id_pseudo_contact: "Псевдоним + приватный email",
  id_pseudo_contact_d:
    "Email шифруется на хранении, доступен только администраторам, используется лишь для уточнений. Никогда не публикуется.",
  pseudo_label: "Псевдоним",
  contact_label: "Контактный email (только для модераторов)",
  consent_publish:
    "Нажимая «Отправить», вы соглашаетесь на публикацию на madcaptains.net после ручной модерации и редактирования.",
  consent_redact: "Согласен(на) на редакционное удаление идентифицирующих деталей до публикации",
  consent_translate: "Согласен(на) на перевод на другие языки",
  review_h: "Проверить и отправить",
  review_p:
    "Это прочтёт живой модератор. Идентифицирующие детали удаляются до публикации. Если что-то опубликовать нельзя, вы получите аргументированный отказ (только если вы указали email). В остальных случаях запись публикуется тихо.",
  sum_type: "Тип", sum_lang: "Язык", sum_length: "Длина",
  sum_categories: "Категории", sum_identity: "Идентичность",
  sum_chars: (n) => `${n} символов`,
  translate_all: "Перевести на все языки сайта",
  translate_all_d:
    "После публикации запустить ИИ-переводы на все активные локали. Каждый перевод помечен как ИИ; человек-переводчик может его доработать. Включено по умолчанию.",
  challenge_legend: "Одна быстрая проверка — не Google reCAPTCHA",
  send: "Отправить историю", sending: "Отправка…",
  back: "← Назад", cont: "Дальше →",
  sent_h: "Принято.",
  sent_p:
    "Ваша история в очереди на модерацию. Её прочтёт человек. Если вы оставили email и потребуется уточнение — мы напишем. В остальных случаях следите за архивом: запись может появиться под именем Anonymous seafarer или выбранным псевдонимом.",
  sent_read: "Открыть архив", sent_help: "Получить помощь",
};

const STRINGS_FR: WizardStrings = {
  privacy: "Aucune adresse IP · Aucune analytique · Aucun fingerprinting sur cette page",
  safety_h: "Avant de commencer",
  safety_p:
    "Vous pouvez raconter votre histoire sans donner votre nom, le nom de votre navire, ni aucun détail permettant de vous identifier. Nous vous aiderons à retirer les détails identifiants. C'est gratuit et sans compte.",
  safety_help: "En danger maintenant ? → Ouvrir l'aide",
  safety_q: "De quel côté êtes-vous dans cette histoire ?",
  side_victim: "J'ai vécu cela",
  side_victim_d: "Moi-même ou quelqu'un dont j'ai été témoin étions du côté de la victime.",
  side_confessor: "J'étais de l'autre côté",
  side_confessor_d: "J'ai agi en tant qu'auteur et je veux le consigner. Anonyme, sans jugement.",
  lang_h: "Dans quelle langue allez-vous écrire ?",
  lang_p: "Écrivez dans la langue dans laquelle vous pensez et rêvez. Nous traduirons.",
  story_h: "Votre histoire",
  story_p:
    "Que s'est-il passé ? Sur quelle période ? Quel impact cela a-t-il eu sur vous ? Comment cela s'est-il terminé ? Minimum 300 caractères. Fermer cet onglet efface votre brouillon — c'est voulu.",
  story_placeholder: "Écrivez dans votre langue. Le compagnon à droite est facultatif.",
  story_chars: (n) => `${n} / 300+ caractères`,
  story_too_short: "Écrivez au moins 300 caractères pour continuer.",
  companion_open: "Parler au compagnon IA",
  companion_open_d:
    "Pas un avocat, pas un service de crise, pas un humain. Il ne stockera pas vos mots tant que vous n'aurez pas appuyé sur Envoyer ci-dessous.",
  companion_label: "Compagnon IA",
  companion_send: "Env.",
  companion_placeholder: "Écrire au compagnon…",
  facets_h: "Étiquettes et catégories",
  facets_p:
    "Tout ici est facultatif. Vous pouvez ignorer n'importe quel champ. Région et décennie seulement — jamais assez précis pour identifier un navire.",
  facets_categories: "Catégories",
  facets_skip: "— ignorer —",
  facet_labels: {
    abuser_position: "Poste de l'auteur des faits",
    victim_position: "Poste à bord",
    vessel_type: "Type de navire",
    region: "Région / catégorie de pavillon",
    outcome: "Issue",
    decade: "Période (décennie)",
  },
  identity_h: "Identité et consentement",
  identity_p:
    "La plupart des auteurs restent entièrement anonymes. Un pseudonyme est affiché sur l'entrée ; un email de contact est réservé aux administrateurs et utilisé uniquement si un modérateur a besoin d'une précision.",
  id_anonymous: "Entièrement anonyme",
  id_anonymous_d: "Auteur affiché comme \u00AB Anonymous seafarer \u00BB.",
  id_pseudo: "Pseudonyme",
  id_pseudo_d: "Choisissez un nom. Nous ne le vérifions contre rien.",
  id_pseudo_contact: "Pseudonyme + email de contact privé",
  id_pseudo_contact_d:
    "L'email est chiffré au repos, réservé aux administrateurs, utilisé uniquement pour des clarifications. Jamais affiché.",
  pseudo_label: "Pseudonyme",
  contact_label: "Email de contact (administrateurs uniquement)",
  consent_publish:
    "En appuyant sur Envoyer, vous consentez à la publication sur madcaptains.net après examen humain et rédaction.",
  consent_redact: "Je consens à la rédaction éditoriale des détails identifiants avant publication",
  consent_translate: "Je consens à la traduction dans d'autres langues",
  review_h: "Vérifier et envoyer",
  review_p:
    "Un modérateur humain lira ceci. Les détails identifiants sont retirés avant publication. Si quelque chose ne peut pas être publié, vous recevrez un refus motivé (uniquement si vous avez fourni un email). Sinon, l'entrée est publiée discrètement.",
  sum_type: "Type", sum_lang: "Langue", sum_length: "Longueur",
  sum_categories: "Catégories", sum_identity: "Identité",
  sum_chars: (n) => `${n} caractères`,
  translate_all: "Traduire dans toutes les langues du site",
  translate_all_d:
    "À la publication, mettre en file les traductions IA dans chaque locale active. Chaque traduction est étiquetée IA et un traducteur humain peut l'affiner. Activé par défaut.",
  challenge_legend: "Une vérification rapide — pas de Google reCAPTCHA",
  send: "Envoyer mon histoire", sending: "Envoi…",
  back: "← Retour", cont: "Continuer →",
  sent_h: "Reçu.",
  sent_p:
    "Votre histoire est dans la file de modération. Un humain la lira. Si vous avez fourni un email et qu'une clarification est nécessaire, vous aurez des nouvelles. Sinon, surveillez l'archive : votre entrée peut apparaître sous Anonymous seafarer ou sous le pseudonyme que vous avez choisi.",
  sent_read: "Lire l'archive", sent_help: "Obtenir de l'aide",
};

const STRINGS_ES: WizardStrings = {
  privacy: "Sin direcciones IP · Sin analítica · Sin fingerprinting en esta página",
  safety_h: "Antes de empezar",
  safety_p:
    "Puede contar su historia sin dar su nombre, el nombre de su buque ni ningún detalle que lo identifique. Le ayudaremos a eliminar los detalles identificativos. Es gratis y sin cuenta.",
  safety_help: "¿En peligro ahora? → Abrir Ayuda",
  safety_q: "¿De qué lado de la historia está?",
  side_victim: "Lo viví",
  side_victim_d: "Yo o alguien a quien presencié estuvimos del lado receptor.",
  side_confessor: "Estuve del otro lado",
  side_confessor_d:
    "Actué como el agresor y quiero dejarlo por escrito. Anónimo, sin juicio.",
  lang_h: "¿En qué idioma va a escribir?",
  lang_p: "Escriba en el idioma en el que piensa y sueña. Nosotros traduciremos.",
  story_h: "Su historia",
  story_p:
    "¿Qué ocurrió? ¿Durante qué período? ¿Cómo le afectó? ¿Cómo terminó? Mínimo 300 caracteres. Cerrar esta pestaña borra su borrador — a propósito.",
  story_placeholder: "Escriba en su propio idioma. El acompañante a la derecha es opcional.",
  story_chars: (n) => `${n} / 300+ caracteres`,
  story_too_short: "Escriba al menos 300 caracteres para continuar.",
  companion_open: "Hablar con el acompañante IA",
  companion_open_d:
    "No es abogado, ni servicio de crisis, ni humano. No almacenará sus palabras hasta que pulse Enviar abajo.",
  companion_label: "Acompañante IA",
  companion_send: "Env.",
  companion_placeholder: "Escriba al acompañante…",
  facets_h: "Etiquetas y categorías",
  facets_p:
    "Todo aquí es opcional. Puede omitir cualquier campo. Solo región y década — nunca lo bastante específico para identificar un buque.",
  facets_categories: "Categorías",
  facets_skip: "— omitir —",
  facet_labels: {
    abuser_position: "Cargo del agresor",
    victim_position: "Puesto a bordo",
    vessel_type: "Tipo de buque",
    region: "Región / categoría de pabellón",
    outcome: "Resultado",
    decade: "Período (década)",
  },
  identity_h: "Identidad y consentimiento",
  identity_p:
    "La mayoría de los autores permanecen totalmente anónimos. Un seudónimo se muestra en la entrada; un correo de contacto es sólo para administradores y se usa únicamente si un moderador necesita una aclaración.",
  id_anonymous: "Totalmente anónimo",
  id_anonymous_d: "Autor mostrado como \u00ABAnonymous seafarer\u00BB.",
  id_pseudo: "Seudónimo",
  id_pseudo_d: "Elija un nombre. No lo cotejamos con nada.",
  id_pseudo_contact: "Seudónimo + correo de contacto privado",
  id_pseudo_contact_d:
    "El correo se cifra en reposo, es sólo para administradores y se usa únicamente para aclaraciones. Nunca se muestra.",
  pseudo_label: "Seudónimo",
  contact_label: "Correo de contacto (sólo administradores)",
  consent_publish:
    "Al pulsar Enviar, usted consiente la publicación en madcaptains.net tras revisión y redacción humanas.",
  consent_redact: "Consiento la redacción editorial de detalles identificativos antes de la publicación",
  consent_translate: "Consiento la traducción a otros idiomas",
  review_h: "Revisar y enviar",
  review_p:
    "Un moderador humano leerá esto. Los detalles identificativos se eliminan antes de la publicación. Si algo no puede publicarse, recibirá un rechazo razonado (sólo si proporcionó un correo). De lo contrario, la entrada se publica en silencio.",
  sum_type: "Tipo", sum_lang: "Idioma", sum_length: "Longitud",
  sum_categories: "Categorías", sum_identity: "Identidad",
  sum_chars: (n) => `${n} caracteres`,
  translate_all: "Traducir a todos los idiomas del sitio",
  translate_all_d:
    "Al publicarse, encolar traducciones IA a cada idioma activo. Cada traducción se etiqueta como IA y un traductor humano puede refinarla. Activado por defecto.",
  challenge_legend: "Una comprobación rápida — no es Google reCAPTCHA",
  send: "Enviar mi historia", sending: "Enviando…",
  back: "← Atrás", cont: "Continuar →",
  sent_h: "Recibido.",
  sent_p:
    "Su historia está en la cola de moderación. Un humano la leerá. Si proporcionó un correo y se necesita una aclaración, le escribiremos. De lo contrario, vigile el archivo: su entrada puede aparecer bajo Anonymous seafarer o el seudónimo elegido.",
  sent_read: "Leer el archivo", sent_help: "Obtener ayuda",
};

const STRINGS_ZH: WizardStrings = {
  privacy: "无 IP 地址 · 无分析 · 此页面无指纹识别",
  safety_h: "开始之前",
  safety_p:
    "您可以讲述您的经历，无需透露姓名、船名或任何可识别您身份的细节。我们将协助您删除识别信息。提交免费，无需账户。",
  safety_help: "正处于危险中？→ 打开帮助",
  safety_q: "您处于这段经历的哪一方？",
  side_victim: "我亲身经历过",
  side_victim_d: "我或我目睹的人是承受方。",
  side_confessor: "我曾在另一方",
  side_confessor_d: "我曾作为施害者，我想把它记录下来。匿名，不评判。",
  lang_h: "您将用哪种语言书写？",
  lang_p: "请用您思考和梦境中使用的语言书写。我们将进行翻译。",
  story_h: "您的故事",
  story_p:
    "发生了什么？在何时期？对您有何影响？如何结束？至少 300 个字符。关闭此标签页将清除您的草稿——这是有意为之。",
  story_placeholder: "用您自己的语言书写。右侧的助手是可选的。",
  story_chars: (n) => `${n} / 300+ 字符`,
  story_too_short: "请至少写 300 个字符才能继续。",
  companion_open: "与 AI 助手对话",
  companion_open_d:
    "不是律师，不是危机服务，也不是人类。在您按下下方「发送」之前，它不会存储您的话语。",
  companion_label: "AI 助手",
  companion_send: "发送",
  companion_placeholder: "向助手输入…",
  facets_h: "标签与类别",
  facets_p:
    "此处所有内容均为可选。任何字段都可跳过。仅地区和年代——绝不会具体到能识别某艘船。",
  facets_categories: "类别",
  facets_skip: "— 跳过 —",
  facet_labels: {
    abuser_position: "施害者职位",
    victim_position: "您在船上的职位",
    vessel_type: "船舶类型",
    region: "地区／船旗类别",
    outcome: "结果",
    decade: "时期（年代）",
  },
  identity_h: "身份与同意",
  identity_p:
    "大多数作者完全保持匿名。条目上显示笔名；联系邮箱仅限管理员，仅在版主需要澄清时使用。",
  id_anonymous: "完全匿名",
  id_anonymous_d: "作者显示为「Anonymous seafarer」。",
  id_pseudo: "笔名",
  id_pseudo_d: "选一个名字。我们不会与任何记录核对。",
  id_pseudo_contact: "笔名 + 私密联系邮箱",
  id_pseudo_contact_d:
    "邮箱静态加密存储，仅管理员可见，仅用于澄清。永不显示。",
  pseudo_label: "笔名",
  contact_label: "联系邮箱（仅管理员）",
  consent_publish:
    "按下「发送」即表示您同意经人工审核与编辑后在 madcaptains.net 发布。",
  consent_redact: "我同意在发布前对识别细节进行编辑性删改",
  consent_translate: "我同意翻译成其他语言",
  review_h: "审阅并发送",
  review_p:
    "将由人工版主阅读。识别细节会在发布前删除。若某些内容无法发布，您将收到说明理由的拒绝（仅当您留下联系邮箱）。否则，条目将悄然发布。",
  sum_type: "类型", sum_lang: "语言", sum_length: "长度",
  sum_categories: "类别", sum_identity: "身份",
  sum_chars: (n) => `${n} 字符`,
  translate_all: "翻译为本站所有语言",
  translate_all_d:
    "发布时将 AI 翻译加入每个活动语言的队列。每个翻译均标注为 AI，人工译者可对其润色。默认开启。",
  challenge_legend: "一项快速核验——不是 Google reCAPTCHA",
  send: "发送我的故事", sending: "发送中…",
  back: "← 返回", cont: "继续 →",
  sent_h: "已收到。",
  sent_p:
    "您的故事已进入审核队列，将由人工阅读。如您留下了联系邮箱并需要澄清，我们会联系您。否则请关注档案：您的条目可能以 Anonymous seafarer 或您选择的笔名出现。",
  sent_read: "阅读档案", sent_help: "获取帮助",
};

const STRINGS_AR: WizardStrings = {
  privacy: "بدون عناوين IP · بدون تحليلات · بدون بصمة في هذه الصفحة",
  safety_h: "قبل أن تبدأ",
  safety_p:
    "يمكنك سرد قصتك دون ذكر اسمك أو اسم سفينتك أو أي تفصيل يمكن أن يُعرّفك. سنساعدك على إزالة التفاصيل المُعرِّفة. التقديم مجاني ولا يتطلب حسابًا.",
  safety_help: "في خطر الآن؟ ← افتح المساعدة",
  safety_q: "من أي جانب من القصة أنت؟",
  side_victim: "عشت هذا",
  side_victim_d: "أنا أو شخص شهدته كنا في الطرف المتلقي.",
  side_confessor: "كنت في الطرف الآخر",
  side_confessor_d:
    "تصرّفت كمن أوقع الأذى، وأريد تدوين ذلك. مجهول، بلا حكم.",
  lang_h: "بأي لغة ستكتب؟",
  lang_p: "اكتب باللغة التي تفكر وتحلم بها. سنقوم بالترجمة.",
  story_h: "قصتك",
  story_p:
    "ماذا حدث؟ خلال أي فترة؟ كيف أثّر ذلك عليك؟ كيف انتهى؟ الحد الأدنى 300 حرف. إغلاق هذه التبويبة يمحو مسودتك — هذا متعمد.",
  story_placeholder: "اكتب بلغتك. الرفيق على اليسار اختياري.",
  story_chars: (n) => `${n} / 300+ حرف`,
  story_too_short: "اكتب 300 حرفًا على الأقل للمتابعة.",
  companion_open: "تحدّث مع الرفيق الذكي",
  companion_open_d:
    "ليس محاميًا، وليس خدمة طوارئ، وليس إنسانًا. لن يحفظ كلماتك حتى تضغط \u00AB إرسال \u00BB في الأسفل.",
  companion_label: "رفيق الذكاء الاصطناعي",
  companion_send: "إرسال",
  companion_placeholder: "اكتب للرفيق…",
  facets_h: "الوسوم والفئات",
  facets_p:
    "كل شيء هنا اختياري. يمكن تخطي أي حقل. المنطقة والعقد فقط — ليست محددة بما يكفي للتعرف على سفينة.",
  facets_categories: "الفئات",
  facets_skip: "— تخطٍّ —",
  facet_labels: {
    abuser_position: "منصب المسيء",
    victim_position: "منصبك على متن السفينة",
    vessel_type: "نوع السفينة",
    region: "المنطقة / فئة العلم",
    outcome: "النتيجة",
    decade: "الإطار الزمني (العقد)",
  },
  identity_h: "الهوية والموافقة",
  identity_p:
    "معظم المؤلفين يبقون مجهولين تمامًا. يُعرض اسم مستعار على المُدخل؛ بريد التواصل متاح للمشرفين فقط ويُستخدم عند الحاجة إلى توضيح.",
  id_anonymous: "مجهول تمامًا",
  id_anonymous_d: "يُعرض المؤلف باسم \u00ABAnonymous seafarer\u00BB.",
  id_pseudo: "اسم مستعار",
  id_pseudo_d: "اختر اسمًا. لن نتحقق منه مع أي مصدر.",
  id_pseudo_contact: "اسم مستعار + بريد تواصل خاص",
  id_pseudo_contact_d:
    "البريد مشفّر أثناء التخزين، للمشرفين فقط، ويُستخدم للتوضيح فقط. لا يُعرض أبدًا.",
  pseudo_label: "اسم مستعار",
  contact_label: "بريد التواصل (للمشرفين فقط)",
  consent_publish:
    "بالضغط على \u00AB إرسال \u00BB، فإنك توافق على النشر على madcaptains.net بعد مراجعة بشرية وتحرير.",
  consent_redact: "أوافق على تحرير التفاصيل المُعرِّفة قبل النشر",
  consent_translate: "أوافق على الترجمة إلى لغات أخرى",
  review_h: "المراجعة والإرسال",
  review_p:
    "سيقرأ هذا مشرفٌ بشري. تُزال التفاصيل المُعرِّفة قبل النشر. إذا تعذّر نشر شيء، ستتلقّى رفضًا مُعلَّلًا (فقط إذا قدّمت بريدًا). وإلا تُنشر المُدخلة بهدوء.",
  sum_type: "النوع", sum_lang: "اللغة", sum_length: "الطول",
  sum_categories: "الفئات", sum_identity: "الهوية",
  sum_chars: (n) => `${n} حرف`,
  translate_all: "ترجم إلى جميع لغات الموقع",
  translate_all_d:
    "عند النشر، أضف ترجمات الذكاء الاصطناعي إلى كل اللغات النشطة. تُوسَم كل ترجمة على أنها بالذكاء الاصطناعي، ويمكن لمترجم بشري تحسينها. مفعّل افتراضيًا.",
  challenge_legend: "تحقّق سريع — ليس Google reCAPTCHA",
  send: "أرسل قصتي", sending: "جارٍ الإرسال…",
  back: "→ رجوع", cont: "← متابعة",
  sent_h: "تم الاستلام.",
  sent_p:
    "قصتك في قائمة المراجعة. سيقرأها إنسان. إذا قدّمت بريدًا واحتجنا إلى توضيح، سنراسلك. وإلا تابع الأرشيف: قد تظهر مُدخلتك باسم Anonymous seafarer أو الاسم المستعار الذي اخترته.",
  sent_read: "اقرأ الأرشيف", sent_help: "احصل على مساعدة",
};

const STRINGS_BY_LANG: Record<Lang, WizardStrings> = {
  en: STRINGS_EN,
  ru: STRINGS_RU,
  fr: STRINGS_FR,
  es: STRINGS_ES,
  zh: STRINGS_ZH,
  ar: STRINGS_AR,
};

function pickStrings(lang: Lang): WizardStrings {
  return STRINGS_BY_LANG[lang] ?? STRINGS_EN;
}

// Per-locale labels for FACETS enum values (Bucket B). en/ru fall back to
// the previous `value.replaceAll('_',' ')` rendering; fr/es/zh/ar are
// translated here. `decade` values are numeric (1980s…) and shared across
// locales, so they are intentionally absent.
type FacetOptionDict = Partial<Record<FacetKey, Record<string, string>>>;
const FACET_OPTION_LABELS: Partial<Record<Lang, FacetOptionDict>> = {
  fr: {
    abuser_position: { master: "capitaine", chief_officer: "second capitaine", chief_engineer: "chef mécanicien", other_officer: "autre officier", shore_mgmt: "direction à terre", other: "autre" },
    victim_position: { cadet: "élève officier", rating: "matelot", junior_officer: "officier subalterne", senior_officer: "officier supérieur", catering: "restauration", other: "autre" },
    vessel_type: { bulk: "vraquier", tanker: "pétrolier", container: "porte-conteneurs", general_cargo: "cargo général", passenger: "passagers", offshore: "offshore", fishing: "pêche", other: "autre" },
    region: { european_flag: "pavillon européen", open_registry: "registre ouvert", asia_pacific: "Asie-Pacifique", americas: "Amériques", africa: "Afrique", middle_east: "Moyen-Orient", other: "autre" },
    outcome: { endured: "enduré", left_vessel: "quitté le navire", complaint_filed: "plainte déposée", case_won: "affaire gagnée", case_lost: "affaire perdue", unresolved: "non résolu" },
  },
  es: {
    abuser_position: { master: "capitán", chief_officer: "primer oficial", chief_engineer: "jefe de máquinas", other_officer: "otro oficial", shore_mgmt: "dirección en tierra", other: "otro" },
    victim_position: { cadet: "cadete", rating: "marinero", junior_officer: "oficial subalterno", senior_officer: "oficial superior", catering: "fonda", other: "otro" },
    vessel_type: { bulk: "granelero", tanker: "petrolero", container: "portacontenedores", general_cargo: "carga general", passenger: "pasaje", offshore: "offshore", fishing: "pesca", other: "otro" },
    region: { european_flag: "pabellón europeo", open_registry: "registro abierto", asia_pacific: "Asia-Pacífico", americas: "Américas", africa: "África", middle_east: "Oriente Medio", other: "otro" },
    outcome: { endured: "lo soporté", left_vessel: "dejé el buque", complaint_filed: "denuncia presentada", case_won: "caso ganado", case_lost: "caso perdido", unresolved: "sin resolver" },
  },
  zh: {
    abuser_position: { master: "船长", chief_officer: "大副", chief_engineer: "轮机长", other_officer: "其他高级船员", shore_mgmt: "岸上管理", other: "其他" },
    victim_position: { cadet: "实习生", rating: "普通船员", junior_officer: "初级船员", senior_officer: "高级船员", catering: "餐饮", other: "其他" },
    vessel_type: { bulk: "散货船", tanker: "油轮", container: "集装箱船", general_cargo: "杂货船", passenger: "客船", offshore: "海工船", fishing: "渔船", other: "其他" },
    region: { european_flag: "欧洲船旗", open_registry: "开放船籍", asia_pacific: "亚太", americas: "美洲", africa: "非洲", middle_east: "中东", other: "其他" },
    outcome: { endured: "忍受", left_vessel: "离船", complaint_filed: "已投诉", case_won: "胜诉", case_lost: "败诉", unresolved: "未解决" },
  },
  ar: {
    abuser_position: { master: "ربّان", chief_officer: "ضابط أول", chief_engineer: "رئيس مهندسين", other_officer: "ضابط آخر", shore_mgmt: "إدارة الشاطئ", other: "أخرى" },
    victim_position: { cadet: "متدرّب", rating: "بحّار عادي", junior_officer: "ضابط مبتدئ", senior_officer: "ضابط أول", catering: "إعاشة", other: "أخرى" },
    vessel_type: { bulk: "ناقلة سوائب", tanker: "ناقلة نفط", container: "حاوية", general_cargo: "بضائع عامة", passenger: "ركاب", offshore: "بحري (أوفشور)", fishing: "صيد", other: "أخرى" },
    region: { european_flag: "علم أوروبي", open_registry: "سجل مفتوح", asia_pacific: "آسيا-المحيط الهادئ", americas: "الأمريكتان", africa: "أفريقيا", middle_east: "الشرق الأوسط", other: "أخرى" },
    outcome: { endured: "تحمّلت", left_vessel: "غادرت السفينة", complaint_filed: "قُدِّمت شكوى", case_won: "ربحت القضية", case_lost: "خسرت القضية", unresolved: "لم تُحلّ" },
  },
};
function facetOptionLabel(lang: Lang, key: FacetKey, value: string): string {
  return FACET_OPTION_LABELS[lang]?.[key]?.[value] ?? value.replaceAll("_", " ");
}

// Once the author has picked the language they'll write in, the wizard UI
// should follow that choice — but ONLY for locales we actually have wizard
// translations for. Otherwise we'd show English strings under a Russian/
// Spanish/Chinese chosen flag, which is worse than just keeping the locale
// the visitor navigated in with. Extend WIZARD_TRANSLATED as new wizard
// translations land (next: fr/es/zh/ar per §3 IMO six).
const WIZARD_TRANSLATED = new Set<Lang>(["en", "ru", "fr", "es", "zh", "ar"]);
function effectiveLang(routeLang: Lang, chosen: string): Lang {
  return WIZARD_TRANSLATED.has(chosen as Lang) ? (chosen as Lang) : routeLang;
}

// §6 — full submission wizard. Session-only state (useReducer, no
// localStorage). Welcome screen → Step 0 (safety + type) → language picker →
// Step 1 story (with AI companion) → Step 2 facets → Step 3 identity →
// Step 4 review → sent.

type Step =
  | "welcome"
  | "safety"
  | "language"
  | "story"
  | "facets"
  | "identity"
  | "review"
  | "sent";

type State = {
  step: Step;
  submission_type: SubmissionType;
  language: SubmissionLanguage;
  body: string;
  facets: Partial<Record<FacetKey, string>>;
  category_slugs: string[];
  identity_mode: IdentityMode;
  pseudonym: string;
  author_contact: string;
  consent_publish: boolean;
  consent_redact: boolean;
  consent_translate: boolean;
  translate_all: boolean;
  honeypot: string;
  challenge_answer: string;
  challenge_idx: number;
  started_at: number;
  showCompanion: boolean;
  submitting: boolean;
  error: string | null;
};

type Action =
  | { type: "goto"; step: Step }
  | { type: "set"; patch: Partial<State> }
  | { type: "toggleCategory"; slug: string }
  | { type: "setFacet"; key: FacetKey; value: string | undefined };

function initialState(routeLang?: Lang): State {
  // §6.1 — default the writing language to the route locale so the wizard
  // chrome (review/sent steps, which key off state.language via
  // effectiveLang) matches what the visitor sees in the URL. Without this,
  // /ar/submit would render review_h / send / sent_h in English until the
  // author manually opens the language picker.
  const validLangCodes = new Set(SUBMISSION_LANGUAGES.map((l) => l.code));
  const defaultLanguage: SubmissionLanguage =
    routeLang && validLangCodes.has(routeLang as SubmissionLanguage)
      ? (routeLang as SubmissionLanguage)
      : "en";
  return {
    step: "welcome",
    submission_type: "victim",
    language: defaultLanguage,
    body: "",
    facets: {},
    category_slugs: [],
    identity_mode: "anonymous",
    pseudonym: "",
    author_contact: "",
    consent_publish: true,
    consent_redact: true,
    consent_translate: true,
    translate_all: true,
    honeypot: "",
    challenge_answer: "",
    challenge_idx: Math.floor(Math.random() * CHALLENGES.length),
    started_at: Date.now(),
    showCompanion: false,
    submitting: false,
    error: null,
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "goto":
      return { ...state, step: action.step, error: null };
    case "set":
      return { ...state, ...action.patch };
    case "toggleCategory": {
      const has = state.category_slugs.includes(action.slug);
      return {
        ...state,
        category_slugs: has
          ? state.category_slugs.filter((s) => s !== action.slug)
          : [...state.category_slugs, action.slug].slice(0, 12),
      };
    }
    case "setFacet": {
      const next = { ...state.facets };
      if (action.value) next[action.key] = action.value;
      else delete next[action.key];
      return { ...state, facets: next };
    }
  }
}

export function SubmitWizard({ lang }: { lang: Lang }) {
  const [state, dispatch] = useReducer(reducer, lang, initialState);
  const [welcome, setWelcome] = useState<WelcomeMessage | null>(null);

  useEffect(() => {
    // §6.1 — the route locale wins because the visitor explicitly chose it
    // (e.g. /ru/submit must render Russian even if navigator.languages starts
    // with English). Browser languages are only a fallback when the site
    // locale has no welcome translation. Session-only — no persistence.
    if (typeof navigator === "undefined") {
      setWelcome(pickWelcome(lang));
      return;
    }
    const candidates: string[] = [lang];
    if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
    if (navigator.language) candidates.push(navigator.language);
    for (const tag of candidates) {
      const msg = pickWelcome(tag);
      // pickWelcome falls back to English on no-match; only accept a hit
      // that actually corresponds to the requested tag's base language.
      const base = tag.split("-")[0]?.toLowerCase();
      if (msg.htmlLang.toLowerCase().startsWith(base ?? "") || base === "en") {
        setWelcome(msg);
        return;
      }
    }
    setWelcome(pickWelcome(undefined));
  }, [lang]);

  // Warn before unload if the user has typed something — §6 "closing the
  // tab erases your draft".
  useEffect(() => {
    if (state.body.length < 1 || state.step === "sent") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [state.body.length, state.step]);

  // Task 3 fix — the Send button was staying disabled after browser
  // back/forward. Symptom: a submit that started in-flight (submitting=true)
  // and was interrupted by the visitor navigating away and back leaves
  // `submitting` stuck at true, because bfcache restores React state as it
  // was. Reset the transient submit-lock on mount and whenever the page is
  // restored from bfcache (`pageshow` with `event.persisted === true`).
  // Everything the visitor typed (body, facets, challenge answer) is kept.
  useEffect(() => {
    const reset = () => {
      dispatch({ type: "set", patch: { submitting: false } });
    };
    // Fresh mount: clear any leftover in-flight flag from a prior state
    // snapshot (defensive; useReducer's initial state is already false but
    // this covers any future edit that could seed it differently).
    reset();
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) reset();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (state.step === "welcome") {
    return <WelcomeScreen lang={lang} welcome={welcome} onContinue={() => dispatch({ type: "goto", step: "safety" })} />;
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-10">
      <PrivacyNote lang={lang} />
      <ProgressDots step={state.step} />
      {state.step === "safety" && <SafetyStep state={state} dispatch={dispatch} lang={lang} />}
      {state.step === "language" && <LanguageStep state={state} dispatch={dispatch} lang={lang} />}
      {state.step === "story" && <StoryStep state={state} dispatch={dispatch} lang={effectiveLang(lang, state.language)} />}
      {state.step === "facets" && <FacetsStep state={state} dispatch={dispatch} lang={effectiveLang(lang, state.language)} />}
      {state.step === "identity" && <IdentityStep state={state} dispatch={dispatch} lang={effectiveLang(lang, state.language)} />}
      {state.step === "review" && <ReviewStep state={state} dispatch={dispatch} lang={effectiveLang(lang, state.language)} />}
      {state.step === "sent" && <SentScreen lang={effectiveLang(lang, state.language)} />}
    </div>
  );
}

function PrivacyNote({ lang }: { lang: Lang }) {
  const t = pickStrings(lang);
  return (
    <p className="mb-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">
      {t.privacy}
    </p>
  );
}

const STEP_ORDER: Step[] = ["safety", "language", "story", "facets", "identity", "review"];
function ProgressDots({ step }: { step: Step }) {
  if (step === "welcome" || step === "sent") return null;
  const idx = STEP_ORDER.indexOf(step);
  return (
    <div className="mb-8 flex gap-1.5" aria-label={`Step ${idx + 1} of ${STEP_ORDER.length}`}>
      {STEP_ORDER.map((_, i) => (
        <span
          key={i}
          className="h-0.5 flex-1 rounded-full"
          style={{ background: i <= idx ? "var(--amber)" : "var(--fog, hsl(var(--border)))" }}
        />
      ))}
    </div>
  );
}

// ----- Welcome (§6.1) --------------------------------------------------------

function WelcomeScreen({
  lang,
  welcome,
  onContinue,
}: {
  lang: Lang;
  welcome: WelcomeMessage | null;
  onContinue: () => void;
}) {
  if (!welcome) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center text-sm text-muted-foreground">
        …
      </div>
    );
  }
  return (
    <div
      className="mx-auto max-w-2xl px-5 py-12 md:py-20"
      lang={welcome.htmlLang}
      dir={welcome.dir ?? "ltr"}
    >
      <p className="mb-3 text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--amber)" }}>
        {welcome.nativeName}
      </p>
      <h1 className="font-display text-3xl leading-tight sm:text-4xl">{welcome.heading}</h1>
      <ol className="mt-7 space-y-4 text-[15px] leading-relaxed">
        {welcome.points.map((p, i) => (
          <li key={i} className="flex gap-3">
            <span
              className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: "var(--amber)" }}
              aria-hidden
            />
            <span>{p}</span>
          </li>
        ))}
      </ol>
      <div className="mt-9 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={onContinue}
          className="rounded-sm px-6 py-3 text-sm font-medium"
          style={{
            background: "var(--ink, var(--foreground))",
            color: "var(--paper, var(--background))",
          }}
        >
          {welcome.cta}
        </button>
        <Link
          to="/$lang/help"
          params={{ lang }}
          className="text-sm underline-offset-4 hover:underline"
          style={{ color: "var(--amber)" }}
        >
          {welcome.helpLine}
        </Link>
      </div>
    </div>
  );
}

// ----- Step: safety + submission type (§6 Step 0) ----------------------------

function SafetyStep({
  state,
  dispatch,
  lang,
}: {
  state: State;
  dispatch: React.Dispatch<Action>;
  lang: Lang;
}) {
  const s = pickStrings(lang);
  return (
    <section>
      <h2 className="font-display text-2xl">{s.safety_h}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{s.safety_p}</p>
      <p className="mt-3 text-sm">
        <Link
          to="/$lang/help"
          params={{ lang }}
          className="underline-offset-4 hover:underline"
          style={{ color: "var(--amber)" }}
        >
          {s.safety_help}
        </Link>
      </p>

      <fieldset className="mt-8">
        <legend className="text-sm font-medium">{s.safety_q}</legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {SUBMISSION_TYPES.map((t) => {
            const selected = state.submission_type === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => dispatch({ type: "set", patch: { submission_type: t } })}
                className="rounded-sm border p-4 text-left transition-colors"
                style={{
                  borderColor: selected ? "var(--amber)" : "hsl(var(--border))",
                  background: selected
                    ? "color-mix(in oklab, var(--amber) 6%, transparent)"
                    : "transparent",
                }}
                aria-pressed={selected}
              >
                <div className="text-sm font-medium">
                  {t === "victim" ? s.side_victim : s.side_confessor}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t === "victim" ? s.side_victim_d : s.side_confessor_d}
                </p>
              </button>
            );
          })}
        </div>
      </fieldset>

      <NavRow onNext={() => dispatch({ type: "goto", step: "language" })} t={s} />
    </section>
  );
}

// ----- Step: submission language (§6) ----------------------------------------

function LanguageStep({ state, dispatch, lang }: { state: State; dispatch: React.Dispatch<Action>; lang: Lang }) {
  const s = pickStrings(lang);
  return (
    <section>
      <h2 className="font-display text-2xl">{s.lang_h}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{s.lang_p}</p>
      <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {SUBMISSION_LANGUAGES.map((l) => {
          const selected = state.language === l.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => dispatch({ type: "set", patch: { language: l.code } })}
              className="rounded-sm border px-3 py-2 text-left text-sm transition-colors"
              style={{
                borderColor: selected ? "var(--amber)" : "hsl(var(--border))",
                background: selected
                  ? "color-mix(in oklab, var(--amber) 6%, transparent)"
                  : "transparent",
              }}
              aria-pressed={selected}
              lang={l.code}
            >
              {l.label}
            </button>
          );
        })}
      </div>
      <NavRow
        onBack={() => dispatch({ type: "goto", step: "safety" })}
        onNext={() => dispatch({ type: "goto", step: "story" })}
        t={s}
      />
    </section>
  );
}

// ----- Step: story + AI companion (§6 Step 1) -------------------------------

function StoryStep({
  state,
  dispatch,
  lang,
}: {
  state: State;
  dispatch: React.Dispatch<Action>;
  lang: Lang;
}) {
  const s = pickStrings(lang);
  const [bodyDraft, setBodyDraft] = useState(state.body);
  const bodyDraftRef = useRef(state.body);
  const commitBody = (body = bodyDraftRef.current) => {
    if (body !== state.body) dispatch({ type: "set", patch: { body } });
  };
  const tooShort = bodyDraft.trim().length < 300;
  return (
    <section>
      <h2 className="font-display text-2xl">{s.story_h}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{s.story_p}</p>

      <div className="mt-5 grid gap-4 md:grid-cols-[1fr_320px]">
        <textarea
          defaultValue={state.body}
          onInput={(e) => {
            const body = e.currentTarget.value;
            bodyDraftRef.current = body;
            setBodyDraft(body);
          }}
          onBlur={(e) => commitBody(e.currentTarget.value)}
          rows={16}
          lang={state.language}
          dir={state.language === "ar" ? "rtl" : "ltr"}
          placeholder={s.story_placeholder}
          className="w-full rounded-sm border p-4 font-serif text-base leading-relaxed focus:outline-none focus:ring-1"
          style={{
            borderColor: "hsl(var(--border))",
            outlineColor: "var(--amber)",
            background: "var(--paper, var(--background))",
          }}
        />

        <aside className="rounded-sm border p-3" style={{ borderColor: "hsl(var(--border))" }}>
          {!state.showCompanion ? (
            <button
              type="button"
              onClick={() => dispatch({ type: "set", patch: { showCompanion: true } })}
              className="w-full rounded-sm border border-dashed px-3 py-4 text-left text-xs"
              style={{ borderColor: "var(--amber)", color: "var(--amber)" }}
            >
              <strong className="block text-sm font-medium" style={{ color: "var(--foreground)" }}>
                {s.companion_open}
              </strong>
              <span className="mt-1 block text-xs text-muted-foreground">{s.companion_open_d}</span>
            </button>
          ) : (
            <Companion mode={state.submission_type} language={state.language} lang={lang} s={s} />
          )}
        </aside>
      </div>

      <div className="mt-2 text-xs text-muted-foreground">
        {s.story_chars(bodyDraft.length)}
      </div>

      <NavRow
        onBack={() => {
          commitBody();
          dispatch({ type: "goto", step: "language" });
        }}
        onNext={tooShort ? undefined : () => {
          commitBody();
          dispatch({ type: "goto", step: "facets" });
        }}
        nextLabel={s.cont}
        nextDisabledHint={tooShort ? s.story_too_short : undefined}
        t={s}
      />
    </section>
  );
}

function Companion({
  mode,
  language,
  lang,
  s,
}: {
  mode: SubmissionType;
  language: SubmissionLanguage;
  lang: Lang;
  s: WizardStrings;
}) {
  // Opening message localized — short canonical EN, RU. Other languages
  // (fr/es/zh/ar) localized per §3 IMO six.
  const OPENERS: Record<Lang, string> = {
    en: "I'm an AI companion — not a human, not a lawyer, not a crisis service. I'm here to help you tell your story. Nothing here is stored until you press Send on the form itself. If you are in danger now, open Help in a new tab first.",
    ru: "Я ИИ-собеседник — не человек, не юрист, не служба кризисной помощи. Я здесь, чтобы помочь вам рассказать. Ваши слова не сохраняются, пока вы не нажмёте «Отправить» на форме. Если опасность сейчас — сначала откройте «Помощь».",
    fr: "Je suis un compagnon IA — pas un humain, pas un avocat, pas un service de crise. Je suis là pour vous aider à raconter votre histoire. Rien n'est stocké ici tant que vous n'appuyez pas sur Envoyer dans le formulaire. Si vous êtes en danger maintenant, ouvrez d'abord Aide dans un nouvel onglet.",
    es: "Soy un acompañante IA — no soy humano, ni abogado, ni servicio de crisis. Estoy aquí para ayudarle a contar su historia. Nada de aquí se guarda hasta que pulse Enviar en el propio formulario. Si está en peligro ahora, abra primero Ayuda en una pestaña nueva.",
    zh: "我是 AI 助手——不是人类，不是律师，也不是危机服务。我在这里帮助您讲述您的经历。在您按下表单上的「发送」之前，这里的内容不会被存储。如果您现在处于危险中，请先在新标签页中打开「帮助」。",
    ar: "أنا رفيق ذكاء اصطناعي — لست إنسانًا ولا محاميًا ولا خدمة طوارئ. أنا هنا لأساعدك على سرد قصتك. لا يُحفظ هنا شيء حتى تضغط «إرسال» على النموذج نفسه. إذا كنت في خطر الآن، افتح صفحة المساعدة في تبويبة جديدة أولًا.",
  };
  const opener = OPENERS[lang] ?? OPENERS.en;

  const transport = useMemo(
    () => new DefaultChatTransport({ api: "/api/submit-companion", body: { mode } }),
    [mode],
  );

  const { messages, sendMessage, status } = useChat({
    id: `submit-companion-${mode}-${language}`,
    transport,
    messages: [
      {
        id: "opener",
        role: "assistant",
        parts: [{ type: "text", text: opener }],
      },
    ],
  });

  const [input, setInput] = useState("");
  const isLoading = status === "submitted" || status === "streaming";
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, status]);

  return (
    <div className="flex h-[28rem] flex-col text-sm">
      <div className="flex items-center justify-between border-b pb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        <span>{s.companion_label}</span>
        <Link to="/$lang/help" params={{ lang }} className="underline-offset-4 hover:underline" style={{ color: "var(--amber)" }}>
          /help
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto py-3 pr-1">
        {messages.map((m) => {
          const text = m.parts
            .map((p) => (p.type === "text" ? p.text : ""))
            .join("");
          return (
            <div key={m.id} className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {(m.role as string) === "user" ? "you" : "ai"}
              </div>
              <div className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed">{text}</div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!input.trim() || isLoading) return;
          const text = input.trim();
          setInput("");
          void sendMessage({ text });
        }}
        className="mt-2 flex gap-2 border-t pt-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isLoading}
          placeholder={s.companion_placeholder}
          className="flex-1 rounded-sm border px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1"
          style={{ borderColor: "hsl(var(--border))", outlineColor: "var(--amber)" }}
        />
        <button
          type="submit"
          disabled={isLoading || !input.trim()}
          className="rounded-sm px-3 py-1.5 text-[13px] disabled:opacity-40"
          style={{ background: "var(--ink, var(--foreground))", color: "var(--paper, var(--background))" }}
        >
          {isLoading ? "…" : s.companion_send}
        </button>
      </form>
    </div>
  );
}

// ----- Step: facets + categories (§5.2 / §5.3) -------------------------------

function FacetsStep({ state, dispatch, lang }: { state: State; dispatch: React.Dispatch<Action>; lang: Lang }) {
  const s = pickStrings(lang);
  return (
    <section>
      <h2 className="font-display text-2xl">{s.facets_h}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{s.facets_p}</p>

      <div className="mt-6">
        <div className="text-sm font-medium">{s.facets_categories}</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const selected = state.category_slugs.includes(c.slug);
            const catLabel = (c.label as Record<string, string>)[lang] ?? c.label.en;
            return (
              <button
                key={c.slug}
                type="button"
                onClick={() => dispatch({ type: "toggleCategory", slug: c.slug })}
                className="rounded-full border px-3 py-1 text-xs"
                style={{
                  borderColor: selected ? "var(--amber)" : "hsl(var(--border))",
                  background: selected
                    ? "color-mix(in oklab, var(--amber) 8%, transparent)"
                    : "transparent",
                }}
                aria-pressed={selected}
              >
                {catLabel}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {(Object.keys(FACETS) as FacetKey[]).map((key) => (
          <label key={key} className="block text-sm">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {s.facet_labels[key]}
            </span>
            <select
              value={state.facets[key] ?? ""}
              onChange={(e) =>
                dispatch({ type: "setFacet", key, value: e.target.value || undefined })
              }
              className="mt-1 w-full rounded-sm border bg-transparent px-2 py-2 text-sm"
              style={{ borderColor: "hsl(var(--border))" }}
            >
              <option value="">{s.facets_skip}</option>
              {FACETS[key].map((v) => (
                <option key={v} value={v}>
                  {facetOptionLabel(lang, key, v)}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <NavRow
        onBack={() => dispatch({ type: "goto", step: "story" })}
        onNext={() => dispatch({ type: "goto", step: "identity" })}
        t={s}
      />
    </section>
  );
}

// ----- Step: identity & consent (§6 Step 3) ----------------------------------

function IdentityStep({ state, dispatch, lang }: { state: State; dispatch: React.Dispatch<Action>; lang: Lang }) {
  const s = pickStrings(lang);
  return (
    <section>
      <h2 className="font-display text-2xl">{s.identity_h}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{s.identity_p}</p>

      <fieldset className="mt-6 space-y-3">
        {IDENTITY_MODES.map((m) => (
          <label
            key={m}
            className="flex cursor-pointer items-start gap-3 rounded-sm border p-3 text-sm"
            style={{
              borderColor: state.identity_mode === m ? "var(--amber)" : "hsl(var(--border))",
            }}
          >
            <input
              type="radio"
              name="identity_mode"
              checked={state.identity_mode === m}
              onChange={() => dispatch({ type: "set", patch: { identity_mode: m } })}
              className="mt-1"
            />
            <span>
              <strong className="block text-sm">
                {m === "anonymous" && s.id_anonymous}
                {m === "pseudonym" && s.id_pseudo}
                {m === "pseudonym_contact" && s.id_pseudo_contact}
              </strong>
              <span className="block text-xs text-muted-foreground">
                {m === "anonymous" && s.id_anonymous_d}
                {m === "pseudonym" && s.id_pseudo_d}
                {m === "pseudonym_contact" && s.id_pseudo_contact_d}
              </span>
            </span>
          </label>
        ))}
      </fieldset>

      {state.identity_mode !== "anonymous" && (
        <label className="mt-4 block text-sm">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {s.pseudo_label}
          </span>
          <input
            type="text"
            value={state.pseudonym}
            onChange={(e) => dispatch({ type: "set", patch: { pseudonym: e.target.value } })}
            maxLength={80}
            className="mt-1 w-full rounded-sm border bg-transparent px-2 py-2 text-sm"
            style={{ borderColor: "hsl(var(--border))" }}
          />
        </label>
      )}
      {state.identity_mode === "pseudonym_contact" && (
        <label className="mt-4 block text-sm">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {s.contact_label}
          </span>
          <input
            type="email"
            value={state.author_contact}
            onChange={(e) => dispatch({ type: "set", patch: { author_contact: e.target.value } })}
            maxLength={255}
            className="mt-1 w-full rounded-sm border bg-transparent px-2 py-2 text-sm"
            style={{ borderColor: "hsl(var(--border))" }}
          />
        </label>
      )}

      <div className="mt-6 space-y-3 text-sm">
        {/* Publication consent is mandatory and implied by pressing Send;
            shown as plain text per §6 instead of a disabled checkbox. */}
        <p
          className="rounded-sm border-l-2 p-3 text-sm"
          style={{
            borderColor: "var(--amber)",
            background: "color-mix(in oklab, var(--amber) 5%, transparent)",
          }}
        >
          {s.consent_publish}
        </p>
        <CheckRow
          checked={state.consent_redact}
          onChange={(v) => dispatch({ type: "set", patch: { consent_redact: v } })}
          label={s.consent_redact}
        />
        <CheckRow
          checked={state.consent_translate}
          onChange={(v) => dispatch({ type: "set", patch: { consent_translate: v } })}
          label={s.consent_translate}
        />
      </div>

      <NavRow
        onBack={() => dispatch({ type: "goto", step: "facets" })}
        onNext={() => dispatch({ type: "goto", step: "review" })}
        t={s}
      />
    </section>
  );
}

function CheckRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-start gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span>{label}</span>
    </label>
  );
}

// ----- Step: review & send (§6 Step 4) ---------------------------------------

function ReviewStep({ state, dispatch, lang }: { state: State; dispatch: React.Dispatch<Action>; lang: Lang }) {
  const s = pickStrings(lang);
  const fn = submitStory;
  const challenge = CHALLENGES[state.challenge_idx];
  const challengeOk = challenge.a.some(
    (a) => a.toLowerCase() === state.challenge_answer.trim().toLowerCase(),
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Client-side challenge validation: reject mismatches before bothering
    // the server. The server re-checks against the same challenge_idx.
    const norm = state.challenge_answer.trim().toLowerCase();
    const ok = challenge.a.some((a) => a.toLowerCase() === norm);
    if (!ok) {
      const CHAL_ERR: Record<Lang, string> = {
        en: "Challenge answer is incorrect.",
        ru: "Ответ на проверочный вопрос неверный.",
        fr: "La réponse au défi est incorrecte.",
        es: "La respuesta del desafío es incorrecta.",
        zh: "验证问题的答案不正确。",
        ar: "إجابة سؤال التحقّق غير صحيحة.",
      };
      dispatch({
        type: "set",
        patch: {
          error: CHAL_ERR[lang] ?? CHAL_ERR.en,
        },
      });
      return;
    }
    dispatch({ type: "set", patch: { submitting: true, error: null } });
    try {
      await fn({
        data: {
          submission_type: state.submission_type,
          language: state.language,
          body: state.body,
          facets: state.facets,
          category_slugs: state.category_slugs,
          identity_mode: state.identity_mode,
          pseudonym: state.pseudonym || undefined,
          author_contact: state.author_contact || undefined,
          consent_publish: true,
          consent_redact: state.consent_redact,
          consent_translate: state.consent_translate,
          translate_all: state.translate_all,
          honeypot: state.honeypot,
          challenge_answer: state.challenge_answer,
          challenge_idx: state.challenge_idx,
          started_at: state.started_at,
        },
      });
      dispatch({ type: "goto", step: "sent" });
    } catch (err) {
      const FAIL: Record<Lang, string> = {
        en: "Submission failed. Please try again.",
        ru: "Не удалось отправить. Попробуйте ещё раз.",
        fr: "L'envoi a échoué. Veuillez réessayer.",
        es: "El envío falló. Inténtelo de nuevo.",
        zh: "提交失败。请重试。",
        ar: "فشل الإرسال. حاول مرة أخرى.",
      };
      const msg = err instanceof Error ? err.message : (FAIL[lang] ?? FAIL.en);
      dispatch({ type: "set", patch: { submitting: false, error: msg } });
    }
  }

  return (
    <section>
      <h2 className="font-display text-2xl">{s.review_h}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{s.review_p}</p>

      <div className="mt-6 space-y-2 rounded-sm border p-4 text-sm" style={{ borderColor: "hsl(var(--border))" }}>
        <Summary k={s.sum_type} v={state.submission_type === "victim" ? s.side_victim : s.side_confessor} />
        <Summary k={s.sum_lang} v={state.language} />
        <Summary k={s.sum_length} v={s.sum_chars(state.body.length)} />
        <Summary k={s.sum_categories} v={state.category_slugs.join(", ") || "—"} />
        <Summary k={s.sum_identity} v={state.identity_mode} />
      </div>

      <form onSubmit={onSubmit} className="mt-6 space-y-5">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.translate_all}
            onChange={(e) => dispatch({ type: "set", patch: { translate_all: e.target.checked } })}
            className="mt-1"
          />
          <span>
            <strong className="block text-sm">{s.translate_all}</strong>
            <span className="block text-xs text-muted-foreground">{s.translate_all_d}</span>
          </span>
        </label>

        <fieldset className="rounded-sm border p-4" style={{ borderColor: "hsl(var(--border))" }}>
          <legend className="px-1 text-xs uppercase tracking-wider text-muted-foreground">
            {s.challenge_legend}
          </legend>
          <p className="text-sm">
            {challenge.q[lang] ?? challenge.q.en}
            {lang !== "en" && (
              <span className="text-muted-foreground"> / {challenge.q.en}</span>
            )}
          </p>
          <input
            type="text"
            value={state.challenge_answer}
            onChange={(e) => dispatch({ type: "set", patch: { challenge_answer: e.target.value } })}
            className="mt-2 w-full max-w-xs rounded-sm border bg-transparent px-2 py-2 text-sm"
            style={{ borderColor: "hsl(var(--border))" }}
            autoComplete="off"
          />
        </fieldset>

        {/* honeypot — hidden from humans, must stay empty */}
        <div aria-hidden style={{ position: "absolute", left: "-9999px" }}>
          <label>
            Your website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={state.honeypot}
              onChange={(e) => dispatch({ type: "set", patch: { honeypot: e.target.value } })}
            />
          </label>
        </div>

        {state.error && (
          <p
            role="alert"
            className="rounded-sm border-l-2 p-3 text-sm"
            style={{ borderColor: "var(--rust)", background: "color-mix(in oklab, var(--rust) 6%, transparent)" }}
          >
            {state.error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <button
            type="button"
            onClick={() => dispatch({ type: "goto", step: "identity" })}
            className="rounded-sm border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
            style={{ borderColor: "hsl(var(--border))", color: "var(--foreground)" }}
          >
            {s.back}
          </button>
          <button
            type="submit"
            disabled={state.submitting || !challengeOk}
            className="rounded-sm px-6 py-3 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--ink, var(--foreground))",
              color: "var(--paper, var(--background))",
            }}
          >
            {state.submitting ? s.sending : s.send}
          </button>
        </div>
      </form>
    </section>
  );
}

function Summary({ k, v }: { k: string; v: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div>
      <div>{v}</div>
    </div>
  );
}

function SentScreen({ lang }: { lang: Lang }) {
  const s = pickStrings(lang);
  return (
    <div className="mx-auto max-w-2xl py-16 text-center">
      <h2 className="font-display text-3xl">{s.sent_h}</h2>
      <p className="mt-4 text-sm text-muted-foreground">{s.sent_p}</p>
      <div className="mt-8 flex flex-wrap justify-center gap-4">
        <Link
          to="/$lang/stories"
          params={{ lang }}
          className="rounded-sm border px-5 py-2.5 text-sm"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          {s.sent_read}
        </Link>
        <Link
          to="/$lang/help"
          params={{ lang }}
          className="text-sm underline-offset-4 hover:underline"
          style={{ color: "var(--amber)" }}
        >
          {s.sent_help}
        </Link>
      </div>
    </div>
  );
}

// ----- Shared nav row --------------------------------------------------------

function NavRow({
  onBack,
  onNext,
  nextLabel,
  nextDisabledHint,
  t,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabledHint?: string;
  t?: WizardStrings;
}) {
  return (
    <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="rounded-sm border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
          style={{ borderColor: "hsl(var(--border))", color: "var(--foreground)" }}
        >
          {t?.back ?? "← Back"}
        </button>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-3">
        {nextDisabledHint && (
          <span className="text-xs text-muted-foreground">{nextDisabledHint}</span>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={!onNext}
          className="rounded-sm px-5 py-2.5 text-sm font-medium disabled:opacity-40"
          style={{
            background: "var(--ink, var(--foreground))",
            color: "var(--paper, var(--background))",
          }}
        >
          {nextLabel ?? t?.cont ?? "Continue →"}
        </button>
      </div>
    </div>
  );
}